import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Forms → Upload a template.
// Fixture: `uploadAFormTemplate` (one existing template, no reports). The scene
// uploads the fictional Riverton PD tow sheet, a real fillable PDF served as a
// static file from /academy/samples, then opens the template it creates.
const PDF_URL = "/academy/samples/vehicle-tow-sheet.pdf";
const FILE_INPUT = `${sel("formUploadDrop")} input[type="file"]`;
const TRAY_LINK = `${sel("uploadTray")} li button`;

const scene: Scene = {
  id: "upload-a-form-template",
  title: "Upload a template",
  startRoute: "/forms",
  fixture: "uploadAFormTemplate",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "A form template is a PDF plus a map of where each answer goes. You upload the blank form once, and every fill after that reuses the same map. I'll bring in a vehicle tow sheet.",
      actions: [
        { type: "waitFor", target: sel("formsUploadButton") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "open",
      narration: "On the Forms page, click Upload Template.",
      actions: [
        { type: "moveTo", target: sel("formsUploadButton") },
        // "click Upload Template": the click lands near 1.5 s of the 3 s clip.
        { type: "wait", ms: 900 },
        { type: "click", target: sel("formsUploadButton") },
        { type: "waitFor", target: sel("formUploadDrop") },
      ],
    },
    {
      id: "choose",
      narration:
        "Pick your PDF, or drag it onto the dialog. This one is a fillable PDF, so the app reads its fields right away and tells me it found twenty.",
      actions: [
        { type: "moveTo", target: sel("formUploadDrop") },
        { type: "spotlight", target: sel("formUploadDrop") },
        // "This one is a fillable PDF" starts at 3.6 s: pick the file then.
        { type: "wait", ms: 2800 },
        { type: "attachFile", target: FILE_INPUT, url: PDF_URL, name: "vehicle-tow-sheet.pdf", mime: "application/pdf" },
        { type: "waitFor", target: sel("formUploadDetected"), timeoutMs: 15000 },
        { type: "spotlight", target: sel("formUploadDetected") },
        { type: "wait", ms: 4000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "details",
      narration:
        "The name comes from the file, and I can add a short description. Then I choose the scope. Personal is just for me. Agency shares the form with the whole department.",
      actions: [
        // "add a short description" starts at 1.75 s; "Then I choose the scope" at 4.0 s.
        { type: "moveTo", target: sel("formUploadName") },
        { type: "wait", ms: 1000 },
        {
          type: "type",
          target: sel("formUploadDescription"),
          text: "Vehicle tow sheet for impounds and evidence holds.",
          cps: 22,
        },
        { type: "moveTo", target: sel("formUploadScope") },
        { type: "spotlight", target: sel("formUploadScope") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "upload",
      narration:
        "Now I click Upload with 20 Fields. A tray shows the upload, then the app reading the form and grouping the fields into sections.",
      actions: [
        { type: "moveTo", target: sel("formUploadSubmit") },
        // "Now I click Upload with 20 Fields" is said between 0.8 s and 2.7 s.
        { type: "wait", ms: 1000 },
        { type: "click", target: sel("formUploadSubmit") },
        { type: "waitFor", target: sel("uploadTray") },
        { type: "spotlight", target: sel("uploadTray") },
        { type: "waitFor", target: TRAY_LINK, timeoutMs: 20000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "review",
      narration:
        "When it finishes, the new template is at the top of the list. Opening it shows how many fields were found and how they're grouped.",
      actions: [
        // "Opening it shows..." starts at 3.7 s.
        { type: "moveTo", target: sel("formTemplateName") },
        { type: "wait", ms: 2800 },
        { type: "click", target: sel("formTemplateName") },
        { type: "waitFor", target: sel("formTemplateStats"), timeoutMs: 15000 },
        { type: "spotlight", target: sel("formTemplateStats") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "edit",
      narration:
        "If anything was missed, Edit Fields opens the designer, where you can add or fix a field. A scanned, flat PDF works too. The app just proposes the fields for you instead of reading them.",
      actions: [
        // "Edit Fields opens the designer" starts at 1.6 s; "A scanned, flat PDF" at 5.7 s.
        { type: "wait", ms: 1000 },
        { type: "moveTo", target: sel("formEditFields") },
        { type: "spotlight", target: sel("formEditFields") },
        { type: "wait", ms: 4000 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
